import { useState, useCallback, useEffect } from 'react';
import { supabase } from '../lib/supabase';

/**
 * Wrapper rond Supabase Auth MFA (TOTP). Bevat alleen enrollment/beheer —
 * de login-uitdaging (aal2) wordt apart afgehandeld in de login-flow.
 */
export interface EnrollResult {
  factorId: string;
  qrCode: string; // SVG data-URL, direct in een <img> te gebruiken
  secret: string; // handmatige invoer (1Password / Yubico Authenticator)
  uri: string;
}

export function useMfa() {
  const [factorId, setFactorId] = useState<string | null>(null);
  const [verified, setVerified] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) throw error;
      const totp = data?.totp ?? [];
      const active = totp.find((f) => f.status === 'verified') ?? totp[0] ?? null;
      setFactorId(active?.id ?? null);
      setVerified(active?.status === 'verified');
    } catch {
      setFactorId(null);
      setVerified(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  /** Start enrollment: maakt een (nog niet-geverifieerde) TOTP-factor + QR. */
  const enroll = useCallback(async (): Promise<EnrollResult> => {
    // Ruim eventuele eerdere, niet-geverifieerde factor op (voorkomt "factor already exists").
    const { data: existing } = await supabase.auth.mfa.listFactors();
    const stale = (existing?.totp ?? []).find((f) => f.status !== 'verified');
    if (stale) {
      await supabase.auth.mfa.unenroll({ factorId: stale.id });
    }
    const { data, error } = await supabase.auth.mfa.enroll({ factorType: 'totp' });
    if (error) throw error;
    return {
      factorId: data.id,
      qrCode: data.totp.qr_code,
      secret: data.totp.secret,
      uri: data.totp.uri,
    };
  }, []);

  /** Bevestig enrollment met de 6-cijferige code uit de authenticator-app. */
  const verifyEnroll = useCallback(
    async (id: string, code: string) => {
      const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId: id,
      });
      if (challengeError) throw challengeError;
      const { error } = await supabase.auth.mfa.verify({
        factorId: id,
        challengeId: challenge.id,
        code: code.trim(),
      });
      if (error) throw error;
      await refresh();
    },
    [refresh],
  );

  /** Verwijder een factor (MFA uitschakelen). */
  const unenroll = useCallback(
    async (id: string) => {
      const { error } = await supabase.auth.mfa.unenroll({ factorId: id });
      if (error) throw error;
      await refresh();
    },
    [refresh],
  );

  return { factorId, verified, loading, refresh, enroll, verifyEnroll, unenroll };
}
